/**
 * Phase 45.1b — pure helpers for the SSO browser-navigation legs.
 *
 * The IdP handoff is a full-page navigation (302 chain through the API),
 * not an XHR: the browser must land on the IdP's login page. `API_BASE_URL`
 * already carries the `/api/v1` prefix (`/api/v1` in dev via the Vite
 * proxy; `https://api.<domain>/api/v1` in prod), so the start URL is a
 * straight concatenation.
 */

/**
 * Client-side guard for the post-login `returnTo` target. The server
 * validates it too (`safeReturnPath`), but we never trust a redirect
 * target off the URL bar: only in-app absolute paths survive — anything
 * protocol-relative (`//evil.com`), absolute (`https://…`), or relative
 * collapses to '/'.
 */
export function sanitizeReturnTo(raw: string | null | undefined): string {
  if (!raw) return '/';
  const trimmed = raw.trim();
  if (!trimmed.startsWith('/')) return '/';
  if (trimmed.startsWith('//')) return '/';
  if (trimmed.includes('\\')) return '/';
  return trimmed;
}

/** Browser-navigation URL that kicks off the SP-initiated IdP redirect. */
export function ssoStartUrl(
  apiBaseUrl: string,
  connectionId: string,
  returnTo: string,
): string {
  const base = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
  const params = new URLSearchParams({ returnTo: sanitizeReturnTo(returnTo) });
  return `${base}/auth/sso/connections/${connectionId}/start?${params.toString()}`;
}
